'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Star, MapPin, Phone, Globe, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import TierBadge from '@/components/ui/TierBadge';
import VerifiedBadge from '@/components/ui/VerifiedBadge';

const featuredResults = [
{
  id: 'res-001',
  name: 'Sutton Builders Ltd',
  category: 'Trades & Construction',
  tier: 'featured' as const,
  verified: true,
  rating: 4.9,
  reviewCount: 127,
  description: 'Award-winning local builders specialising in extensions, renovations and new builds across West Suffolk since 1987. All work fully insured and guaranteed.',
  address: 'Bury St Edmunds, IP33 1AB',
  phone: '01284 756 234',
  website: 'suttonbuilders.co.uk',
  tags: ['Extensions', 'New Builds', 'Renovations', 'Loft Conversions'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bbd6597d-1772615043570.png",
  imageAlt: 'Construction workers on a building site with scaffolding'
},
{
  id: 'res-002',
  name: 'The Nutshell Bistro',
  category: 'Restaurants & Cafés',
  tier: 'featured' as const,
  verified: true,
  rating: 4.7,
  reviewCount: 213,
  description: 'Contemporary British cuisine in the heart of Bury St Edmunds. Locally sourced ingredients, seasonal menus, private dining available.',
  address: 'Bury St Edmunds, IP33 2AQ',
  phone: '01284 701 111',
  website: 'nutshellbistro.co.uk',
  tags: ['British Cuisine', 'Private Dining', 'Vegetarian Options'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1517867ae-1774737643947.png",
  imageAlt: 'Elegant restaurant interior with warm lighting and set tables'
},
{
  id: 'res-003',
  name: 'Greenfield Accountants',
  category: 'Accountants & Finance',
  tier: 'featured' as const,
  verified: true,
  rating: 4.8,
  reviewCount: 89,
  description: 'Chartered accountants providing tax, payroll and business advisory services to SMEs across West Suffolk for over 20 years.',
  address: 'Newmarket, CB8 8DH',
  phone: '01638 662 900',
  website: 'greenfieldaccountants.co.uk',
  tags: ['Tax Returns', 'Payroll', 'Business Advisory', 'VAT'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1aba9975a-1773132744505.png",
  imageAlt: 'Professional office with accountant reviewing financial documents at desk'
}];


const standardResults = [
{
  id: 'res-004',
  name: 'Haverhill Auto Centre',
  category: 'Automotive',
  tier: 'enhanced' as const,
  verified: true,
  rating: 4.6,
  reviewCount: 174,
  address: 'Haverhill, CB9 9LG',
  phone: '01440 712 500',
  description: 'Full service MOT, servicing and repair centre. All makes and models.',
  image: "https://images.unsplash.com/photo-1727893141025-35d62b3f4a03",
  imageAlt: 'Mechanic working underneath a car in a well-lit garage'
},
{
  id: 'res-005',
  name: 'Mildenhall Garden Centre',
  category: 'Garden & Landscaping',
  tier: 'enhanced' as const,
  verified: false,
  rating: 4.5,
  reviewCount: 96,
  address: 'Mildenhall, IP28 7JY',
  phone: '01638 714 200',
  description: 'West Suffolk\'s largest garden centre with over 2,000 plant varieties and landscaping services.',
  image: "https://images.unsplash.com/photo-1709955168236-cafdb19f58b2",
  imageAlt: 'Colourful garden centre with rows of flowering plants and potted shrubs'
},
{
  id: 'res-006',
  name: 'Newmarket Legal Practice',
  category: 'Legal Services',
  tier: 'enhanced' as const,
  verified: true,
  rating: 4.7,
  reviewCount: 52,
  address: 'Newmarket, CB8 8AA',
  phone: '01638 561 100',
  description: 'Specialist solicitors in property, family law and wills & probate.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1668fa960-1772976670486.png",
  imageAlt: 'Law office with legal books and professional desk setting'
},
{
  id: 'res-007',
  name: 'Clare Cottage Bakery',
  category: 'Restaurants & Cafés',
  tier: 'standard' as const,
  verified: false,
  rating: 4.4,
  reviewCount: 38,
  address: 'Clare, CO10 8PD',
  phone: '01787 277 543',
  description: 'Artisan bread, pastries and cakes baked fresh daily. Café seating available.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17fa9b0dd-1772907439964.png",
  imageAlt: 'Artisan bakery display with fresh bread and pastries'
},
{
  id: 'res-008',
  name: 'Brandon IT Solutions',
  category: 'IT & Technology',
  tier: 'standard' as const,
  verified: false,
  rating: 4.3,
  reviewCount: 27,
  address: 'Brandon, IP27 0AE',
  phone: '01842 810 900',
  description: 'IT support, network setup and cybersecurity for small businesses across West Suffolk.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ad019c19-1772247251042.png",
  imageAlt: 'IT technician working on computer hardware and networking equipment'
}];


const freeResults = [
{ id: 'res-009', name: 'Bury St Edmunds Plumbing', category: 'Trades & Construction', address: 'Bury St Edmunds, IP32', phone: '01284 700 001' },
{ id: 'res-010', name: 'Stanton Electrical Services', category: 'Trades & Construction', address: 'Stanton, IP31', phone: '01359 250 120' },
{ id: 'res-011', name: 'Thurston Village Shop', category: 'Retail & Shopping', address: 'Thurston, IP31', phone: '01359 232 456' },
{ id: 'res-012', name: 'Woolpit Taxi Services', category: 'Automotive', address: 'Woolpit, IP30', phone: '01359 242 789' },
{ id: 'res-013', name: 'Risby Pest Control', category: 'Trades & Construction', address: 'Risby, IP28', phone: '01284 811 234' },
{ id: 'res-014', name: 'Fornham Dog Grooming', category: 'Hair & Beauty', address: 'Fornham, IP28', phone: '01284 769 000' }];


export default function SearchResultsList() {
  const [sortBy, setSortBy] = useState('relevance');
  const [currentPage, setCurrentPage] = useState(1);
  const totalResults = 847;
  const totalPages = 85;

  return (
    <div className="flex-1 min-w-0">
      {/* Sort bar */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          <span className="font-bold text-foreground tabular-nums">{totalResults}</span> businesses found
        </p>
        <div className="flex items-center gap-2">
          <ArrowUpDown size={14} className="text-muted-foreground" />
          <label className="text-sm text-muted-foreground">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="input-field text-sm py-1.5 w-auto">
            
            <option value="relevance">Relevance</option>
            <option value="rating">Highest Rated</option>
            <option value="reviews">Most Reviews</option>
            <option value="name">Name A–Z</option>
            <option value="newest">Newest First</option>
          </select>
        </div>
      </div>

      {/* Featured results */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-px flex-1 bg-warning/30" />
          <span className="text-xs font-bold text-warning uppercase tracking-wider px-2">
            ★ Featured Listings
          </span>
          <div className="h-px flex-1 bg-warning/30" />
        </div>
        <div className="space-y-4">
          {featuredResults.map((biz) =>
          <div key={biz.id} className="card-surface result-card-hover overflow-hidden border-l-4 border-l-warning">
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-52 h-36 sm:h-auto bg-muted shrink-0 overflow-hidden">
                  <img
                  src={biz.image}
                  alt={biz.imageAlt}
                  className="w-full h-full object-cover" />
                
                </div>
                <div className="p-4 flex-1">
                  <div className="flex flex-wrap items-start gap-2 mb-1">
                    <h3 className="font-bold text-base text-foreground">{biz.name}</h3>
                    <TierBadge tier={biz.tier} />
                    {biz.verified && <VerifiedBadge />}
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-primary font-semibold">{biz.category}</span>
                    <div className="flex items-center gap-1">
                      <Star size={12} className="text-warning fill-warning" />
                      <span className="text-sm font-bold tabular-nums">{biz.rating}</span>
                      <span className="text-xs text-muted-foreground">({biz.reviewCount} reviews)</span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 leading-relaxed">{biz.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {biz.tags.map((tag) =>
                  <span
                    key={`${biz.id}-tag-${tag}`}
                    className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                    
                        {tag}
                      </span>
                  )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin size={11} />{biz.address}</span>
                    <span className="flex items-center gap-1"><Phone size={11} />{biz.phone}</span>
                    <span className="flex items-center gap-1"><Globe size={11} />{biz.website}</span>
                  </div>
                </div>
                <div className="p-4 flex sm:flex-col gap-2 sm:justify-center sm:w-36 shrink-0 border-t sm:border-t-0 sm:border-l border-border">
                  <Link href="/business-detail-page" className="btn-primary text-xs py-2 justify-center flex-1 sm:flex-none">
                    View Profile
                  </Link>
                  <a href={`tel:${biz.phone}`} className="btn-secondary text-xs py-2 justify-center flex-1 sm:flex-none">
                    <Phone size={12} />
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Standard/Enhanced results */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-2">
            Enhanced &amp; Standard Listings
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
          {standardResults.map((biz) =>
          <div key={biz.id} className="card-surface result-card-hover overflow-hidden flex flex-col">
              <div className="h-32 bg-muted overflow-hidden">
                <img
                src={biz.image}
                alt={biz.imageAlt}
                className="w-full h-full object-cover" />
              
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="flex items-start gap-2 mb-1">
                  <h3 className="font-bold text-sm text-foreground flex-1 leading-tight">{biz.name}</h3>
                  <TierBadge tier={biz.tier} />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-primary font-semibold">{biz.category}</span>
                  {biz.verified && <VerifiedBadge />}
                </div>
                <div className="flex items-center gap-1 mb-2">
                  <Star size={11} className="text-warning fill-warning" />
                  <span className="text-xs font-bold tabular-nums">{biz.rating}</span>
                  <span className="text-xs text-muted-foreground">({biz.reviewCount})</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3 flex-1 leading-relaxed">{biz.description}</p>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mb-3">
                  <MapPin size={10} />{biz.address}
                </div>
                <Link href="/business-detail-page" className="btn-secondary text-xs py-1.5 justify-center">
                  View Profile
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Free listings */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-2">
            Free Listings
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <div className="card-surface divide-y divide-border">
          {freeResults.map((biz) =>
          <div key={biz.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-sm text-foreground truncate">{biz.name}</span>
                  <TierBadge tier="free" />
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{biz.category}</span>
                  <span className="flex items-center gap-1"><MapPin size={10} />{biz.address}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 ml-4 shrink-0">
                <span className="text-xs text-muted-foreground hidden sm:block">{biz.phone}</span>
                <Link
                href="/sign-up-login-screen"
                className="text-xs text-primary font-semibold hover:underline">
                
                  Upgrade listing →
                </Link>
              </div>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Free listings show limited information.{' '}
          <Link href="/sign-up-login-screen" className="text-primary font-semibold hover:underline">
            Upgrade to Standard
          </Link>{' '}
          for a full business page.
        </p>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <p className="text-sm text-muted-foreground">
          Page <span className="font-bold text-foreground tabular-nums">{currentPage}</span> of{' '}
          <span className="font-bold text-foreground tabular-nums">{totalPages}</span> — {totalResults} results
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn-secondary text-xs py-1.5 px-3 disabled:opacity-40">
            
            <ChevronLeft size={13} />
            Prev
          </button>
          {[1, 2, 3, 4, 5].map((page) =>
          <button
            key={`page-${page}`}
            onClick={() => setCurrentPage(page)}
            className={`w-8 h-8 text-sm font-semibold rounded transition-colors ${
            currentPage === page ?
            'bg-primary text-primary-foreground' :
            'hover:bg-muted text-foreground'}`
            }>
            
              {page}
            </button>
          )}
          <span className="text-muted-foreground text-sm px-1">...</span>
          <button
            className="w-8 h-8 text-sm font-semibold rounded hover:bg-muted text-foreground transition-colors"
            onClick={() => setCurrentPage(totalPages)}>
            
            {totalPages}
          </button>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="btn-secondary text-xs py-1.5 px-3 disabled:opacity-40">
            
            Next
            <ChevronRight size={13} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Per page:</label>
          <select className="input-field text-xs py-1.5 w-auto">
            <option>10</option>
            <option>25</option>
            <option>50</option>
          </select>
        </div>
      </div>
    </div>);

}